import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { Status, Wrapper } from "@googlemaps/react-wrapper";
import styled from "@emotion/styled";
import { Button } from "../Button";
import { Typography } from "../Typography";
import { SvgIcon, SvgNames } from "../../assets/icons/SvgIcon";
import { LoadingIndicator } from "../LoadingIndicator";
import { BRAND_HEROBLUE, WHITE, BRAND_CHARGEBLUE } from "../../design-tokens";
import marker from "../../assets/icons/images/marker.svg";
import markerDot from "../../assets/icons/images/marker-dot.svg";
import blueMarker from "../../assets/icons/images/blueMarker.svg";
import { MapsContext } from "../../context/MapsContext";
import { googleMaps } from "../../adapter/googleMaps";
import { getEnv } from "../../helpers/env";
import {
  getAddressParts,
  missingAddressPartsError,
} from "../../utils/map/address";
import { useNeighborsMapCoords, GetNeighborsMapCoords } from "./MapCoords";

interface MapAction {
  action: () => void;
  title: string;
  testId?: string;
}

interface TestIds {
  zoomIn?: string;
  zoomOut?: string;
  expand?: string;
  marker?: string;
}

type FormModes = "CREATE" | "EDIT" | "READ";

type AddressParts = {
  street: string;
  city: string;
  state: string;
  zip: string;
};

type onDragProps = {
  lat: number;
  lng: number;
  address?: string | undefined;
  addressParts?: AddressParts;
  error?: { message: string } | null;
};

type BaseMapProps = {
  address?: string;
  lat?: string;
  lng?: string;
  formMode?: FormModes;
  editMode?: boolean;
  loading?: boolean;
  loadingErrorMessage?: string | null;
  expandable?: boolean;
  mapAction?: MapAction;
  testIds?: TestIds;
  returnAddressOnDrag?: boolean;
  showEditRevert?: boolean;
  onNewAddress?: (props: onDragProps) => void;
  onChangeMode?: (editMode: boolean) => void;
  getPlaceFromAddress?: boolean;
} & (
  | {
      onDrag: (props: onDragProps) => void;
      renderedFromMapModal?: false | undefined;
    }
  | {
      onDrag?: undefined | ((props: onDragProps) => void);
      renderedFromMapModal?: true;
    }
);

type ShowNearMeMapProps = BaseMapProps & {
  /* "showNearMeButton" only makes sense to be used as prop if
   * it's value is "true", so there is no need to TS this as boolean.
   * If it's value is "false", it should not be passed as prop, and
   * as so, it is handled when MapProps is BaseMapProps instead of ShowNearMeMapProps
   */
  showNearMeButton: true;
  getNeighborsMapCoords: GetNeighborsMapCoords;
};

/* This custom TS is so that if the consumer passes in "showNearMeButton",
 * they are required to pass in "getNeighborsMapCoords" so that
 * the component doesn't break at runtime.
 */
type MapProps = ShowNearMeMapProps | BaseMapProps;

const ResidentMap = ({
  address,
  lat,
  lng,
  editMode = true,
  loading = false,
  loadingErrorMessage = null,
  expandable = true,
  mapAction = undefined,
  onDrag,
  testIds,
  renderedFromMapModal = false,
  returnAddressOnDrag = false,
  onNewAddress,
  showEditRevert = true,
  onChangeMode,
  getPlaceFromAddress = true,
  ...rest
}: MapProps) => {
  const [loadingMyLocation, setLoadingMyLocation] = useState<boolean>(false);
  const { showNearMeButton = false, getNeighborsMapCoords } =
    rest as ShowNearMeMapProps;

  const env = getEnv();
  const { setNewLatLng } = useContext(MapsContext);
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [isEditMode, setIsEditMode] = useState<boolean>(editMode);
  const initialLat = lat ? parseFloat(lat) : undefined;
  const initialLng = lng ? parseFloat(lng) : undefined;
  const [firstLocation, setFirstLocation] = useState<{
    lat: number;
    lng: number;
  }>({ lat: initialLat, lng: initialLng });
  const [status, setStatus] = useState<Status>(Status.LOADING);
  const [mapHasInitiated, setMapHasInitiated] = useState<boolean>(false);
  const mapRef = useRef<HTMLDivElement | null>(null);
  const markerRef = useRef<google.maps.marker.AdvancedMarkerElement>();
  const mapObjRef = useRef<google.maps.Map>();
  const neighborsMarkers = useRef<google.maps.Marker[]>([]);
  const [addressState, setAddressState] = useState<string>(address);
  const [pinMoved, setPinMoved] = useState<boolean>(false);
  const [showSunrunnersNearMe, setShowSunrunnersNearMe] =
    useState<boolean>(false);
  const [showMyLocationButton, setShowMyLocationButton] =
    useState<boolean>(false);
  const initialZoom = 21;
  const firstUpdate = useRef(true);
  const neighborsMapCoords = useNeighborsMapCoords(
    initialLat,
    initialLng,
    getNeighborsMapCoords
  );

  const fitBounds = useCallback(() => {
    const latlngList: google.maps.LatLng[] = [];
    neighborsMapCoords.locations.forEach((location) => {
      latlngList.push(
        new google.maps.LatLng(location.latitude, location.longitude)
      );
    });

    const bounds = new google.maps.LatLngBounds();
    latlngList.forEach(function (n) {
      bounds.extend(n);
    });

    if (neighborsMapCoords.count && mapObjRef.current) {
      mapObjRef.current.panTo(bounds.getCenter());
      mapObjRef.current.fitBounds(bounds, -10);
    } else {
      mapObjRef?.current?.setZoom(10);
    }
  }, [neighborsMapCoords, mapObjRef]);

  useEffect(() => {
    if (
      mapObjRef.current &&
      !showSunrunnersNearMe &&
      initialLat &&
      initialLng
    ) {
      mapObjRef.current.setZoom(initialZoom);
      mapObjRef?.current?.panTo(new google.maps.LatLng(initialLat, initialLng));
    }
    if (showSunrunnersNearMe) {
      fitBounds();
    }
  }, [showSunrunnersNearMe, fitBounds, mapObjRef, initialLat, initialLng]);

  const onMapZoomIn = useCallback(() => {
    if (mapObjRef.current) {
      const currentZoom = mapObjRef.current.getZoom() || 0;
      const allowZoom = !(showSunrunnersNearMe && currentZoom >= 16);
      if (allowZoom) {
        mapObjRef.current.setZoom(currentZoom + 1);
      }
    }
  }, [mapObjRef, showSunrunnersNearMe]);

  useEffect(() => {
    setIsEditMode(editMode);
  }, [editMode]);

  const onMapZoomOut = useCallback(() => {
    if (mapObjRef.current)
      mapObjRef.current.setZoom((mapObjRef.current.getZoom() || 0) - 1);
  }, [mapObjRef]);

  const getGeocode = async ({
    latLng = undefined,
    addressQuery = undefined,
  }) => {
    const newAddress = {
      lat: initialLat,
      lng: initialLng,
      address: undefined,
      addressParts: undefined,
      error: undefined,
    };
    const geocoder = new google.maps.Geocoder();
    if (addressQuery || latLng) {
      await geocoder
        .geocode(
          addressQuery ? { address: addressQuery } : { location: latLng }
        )
        .then((result) => {
          const place = result?.results[0];
          if (place) {
            const { location } = place.geometry;
            const { formatted_address, address_components } = place;
            newAddress.lat = location.lat();
            newAddress.lng = location.lng();
            newAddress.address = formatted_address;
            if (address_components) {
              newAddress.addressParts = getAddressParts(place);
            }
          } else {
            window.alert("No results found");
          }
        })
        .catch((e) => {
          alert("Geocode was not successful for the following reason: " + e);
        });
    }
    return newAddress;
  };

  const syncMap = useCallback(
    (revert = false, enteredAddress = null) => {
      if ((!lat || !lng) && !enteredAddress) {
        setShowMyLocationButton(true);
        return;
      }
      let newAddress;
      if (revert) {
        newAddress = { lat: firstLocation.lat, lng: firstLocation.lng };
        if (onDrag) {
          onDrag(newAddress);
        }
      } else if (enteredAddress) {
        newAddress = enteredAddress;
      } else {
        newAddress = { lat: initialLat, lng: initialLng };
      }

      if (markerRef?.current?.map) {
        markerRef.current.map = null;
      }
      var img = document.createElement("img");
      if (testIds?.marker) {
        img.setAttribute("data-testid", testIds.marker);
      }
      img.src = isEditMode ? marker : blueMarker;
      markerRef.current = new google.maps.marker.AdvancedMarkerElement({
        position: newAddress,
        map: mapObjRef.current,
        gmpDraggable: isEditMode,
        content: img,
      });
      markerRef.current.addListener("dragend", async () => {
        const markerLatRaw = markerRef.current?.position?.lat;
        const markerLngRaw = markerRef.current?.position?.lng;
        const markerLat =
          typeof markerLatRaw === "number" ? markerLatRaw : markerLatRaw();
        const markerLng =
          typeof markerLngRaw === "number" ? markerLngRaw : markerLngRaw();
        setNewLatLng(markerLat as number, markerLng as number);
        if (onDrag) {
          if (returnAddressOnDrag) {
            onDrag(
              await getGeocode({ latLng: { lat: markerLat, lng: markerLng } })
            );
          } else {
            onDrag({ lat: markerLat, lng: markerLng });
          }
        }

        setPinMoved(true);
      });
      if (revert) {
        mapObjRef.current.setCenter(markerRef.current.position);
      }
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [initialLat, initialLng, setNewLatLng, isEditMode, setShowMyLocationButton]
  );

  useEffect(() => {
    // If the lat and lng are not provided, do nothing
    if (!lat || !lng) {
      return;
    }

    const undefinedFirstLocation = !firstLocation.lat && !firstLocation.lng;
    const diffLat = firstLocation.lat !== parseFloat(lat);
    const diffLng = firstLocation.lng !== parseFloat(lng);
    const mapInInitialEditState = isEditMode && !pinMoved;

    if (
      undefinedFirstLocation ||
      (mapInInitialEditState && diffLat && diffLng)
    ) {
      setFirstLocation({ lat: parseFloat(lat), lng: parseFloat(lng) });
    }
  }, [lat, lng, firstLocation, isEditMode, pinMoved]);

  useEffect(() => {
    if (lat && lng && showMyLocationButton) {
      setShowMyLocationButton(false);
    }
  }, [lat, lng, showMyLocationButton]);

  useEffect(() => {
    if (firstUpdate.current) {
      firstUpdate.current = true;
      return;
    }
    syncMap();
  }, [isEditMode, syncMap]);

  const createMapWithGeocode = async ({
    latLng = undefined,
    addressQuery = undefined,
  }) => {
    const newAddress = await getGeocode({ latLng, addressQuery });
    if (newAddress.addressParts) {
      if (onNewAddress) {
        newAddress.error = missingAddressPartsError(newAddress.addressParts);
        onNewAddress(newAddress);
      }
      createMap(newAddress);
      syncMap(false, newAddress);
    }
  };

  const createMap = useCallback(
    (newAddress) => {
      mapObjRef.current = new google.maps.Map(
        mapRef.current as HTMLDivElement,
        {
          mapId: googleMaps[env],
          zoom: initialZoom,
          center: newAddress,
          mapTypeId: "hybrid",
          streetViewControl: false,
          mapTypeControl: false,
          rotateControl: false,
          fullscreenControl: false,
          zoomControl: false,
          tilt: 0,
        }
      );
    },
    [env]
  );

  useEffect(() => {
    if (getPlaceFromAddress && status === Status.SUCCESS && addressState) {
      createMapWithGeocode({ addressQuery: addressState });
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addressState]);

  useEffect(() => {
    neighborsMarkers.current.forEach((marker) => marker.setMap(null));
    neighborsMarkers.current = [];

    if (showSunrunnersNearMe) {
      neighborsMapCoords.locations.forEach((location) => {
        neighborsMarkers.current.push(
          new google.maps.Marker({
            position: {
              lat: location.latitude,
              lng: location.longitude,
            },
            map: mapObjRef.current,
            icon: markerDot,
          })
        );
      });
    }
  }, [neighborsMarkers, neighborsMapCoords, showSunrunnersNearMe]);

  useEffect(() => {
    async function initMap() {
      let newAddress = { lat: initialLat, lng: initialLng };
      createMap(newAddress);
    }

    if (status === Status.SUCCESS && mapRef.current) {
      if (!mapHasInitiated) {
        setMapHasInitiated(true);
        initMap();
      }
      syncMap();
    }
  }, [
    status,
    mapRef,
    initialLat,
    initialLng,
    setNewLatLng,
    mapHasInitiated,
    syncMap,
    createMap,
  ]);

  useEffect(() => {
    if (addressState !== address) {
      setAddressState(address);
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address]);

  const revertCoords = () => {
    setPinMoved(false);
    syncMap(true);
  };

  useEffect(() => {
    if (mapObjRef.current) {
      mapObjRef.current.setZoom(initialZoom);
      mapObjRef?.current?.panTo(new google.maps.LatLng(initialLat, initialLng));
    }
  }, [mapObjRef, initialLat, initialLng]);

  useEffect(() => {
    if (markerRef?.current) {
      markerRef.current.gmpDraggable = isEditMode;
    }
  }, [isEditMode]);

  const setCurrentLocation = () => {
    setLoadingMyLocation(true);
    navigator.geolocation.getCurrentPosition(async (location) => {
      const { latitude, longitude } = location.coords;

      const currentLocation = new google.maps.LatLng(latitude, longitude);
      setLoadingMyLocation(false);
      mapObjRef?.current?.panTo(currentLocation);

      createMapWithGeocode({
        latLng: { lat: latitude, lng: longitude },
      });
    });
    setShowMyLocationButton(false);
  };

  const toggleShowSunrunnersNearMe = () => {
    setShowSunrunnersNearMe(!showSunrunnersNearMe);
  };

  return (
    <Container
      tabIndex={0}
      className={isExpanded ? "fullscreen" : ""}
      data-testid="map-component-container"
    >
      {loading && (
        <NoMapContainer>
          <LoadingIndicator color="black" />
        </NoMapContainer>
      )}
      {loadingErrorMessage && (
        <NoMapContainer>
          <Typography color="red">{loadingErrorMessage}</Typography>
        </NoMapContainer>
      )}
      {!loading && !loadingErrorMessage && (
        <>
          <Wrapper
            apiKey={googleMaps[env]}
            libraries={["places", "marker"]}
            callback={(status: Status) => setStatus(status)}
          >
            <MapWindow className={isExpanded ? "fullscreen" : ""} ref={mapRef}>
              {loadingMyLocation && <LoadingIndicator color="black" />}
              {showMyLocationButton && (
                <ShowLocationButton
                  onClick={setCurrentLocation}
                  size="sm"
                  color="secondary"
                  iconRight={
                    <SvgIcon
                      name={SvgNames.InfoOutline}
                      color={BRAND_HEROBLUE}
                    />
                  }
                  data-testid="map-show-my-location"
                >
                  Show my location
                </ShowLocationButton>
              )}
            </MapWindow>
          </Wrapper>
          {!showMyLocationButton && (
            <MapControlContainer>
              {expandable && !renderedFromMapModal && (
                <MapControlPill>
                  <MapControlButton
                    onClick={() => setIsExpanded(!isExpanded)}
                    iconMiddle={
                      isExpanded ? (
                        <SvgIcon
                          name={SvgNames.Shrink}
                          color={WHITE}
                          style={{
                            backgroundColor: BRAND_HEROBLUE,
                            padding: "5px",
                            borderRadius: "20px",
                          }}
                        />
                      ) : (
                        <SvgIcon
                          name={SvgNames.Expand}
                          color={WHITE}
                          style={{
                            backgroundColor: BRAND_HEROBLUE,
                            padding: "5px",
                            borderRadius: "20px",
                          }}
                        />
                      )
                    }
                    size="sm"
                    color="tertiary"
                    data-testid={testIds?.expand || "map-full-screen"}
                  />
                  {showNearMeButton && (
                    <MapControlButton
                      onClick={toggleShowSunrunnersNearMe}
                      size="sm"
                      color="tertiary"
                      iconMiddle={
                        <SvgIcon
                          name={SvgNames.SIconSvg}
                          viewBox="6 6 20 20"
                          color={WHITE}
                          style={{
                            backgroundColor: showSunrunnersNearMe
                              ? BRAND_CHARGEBLUE
                              : BRAND_HEROBLUE,
                            padding: "5px",
                            borderRadius: "20px",
                          }}
                        />
                      }
                      data-testid="map-show-sunrunners-near-me"
                    />
                  )}
                </MapControlPill>
              )}
              <MapControlPill>
                <MapControlButton
                  onClick={onMapZoomIn}
                  iconMiddle={
                    <SvgIcon
                      name={SvgNames.Plus}
                      color={WHITE}
                      style={{
                        backgroundColor: BRAND_HEROBLUE,
                        padding: "5px",
                        borderRadius: "20px",
                      }}
                      viewBox="0 0 25 24"
                    />
                  }
                  color="tertiary"
                  size="sm"
                  data-testid={testIds?.zoomIn || "map-zoom-in"}
                />
                <MapControlButton
                  onClick={onMapZoomOut}
                  iconMiddle={
                    <SvgIcon
                      name={SvgNames.Subtract}
                      color={WHITE}
                      style={{
                        backgroundColor: BRAND_HEROBLUE,
                        padding: "5px",
                        borderRadius: "20px",
                      }}
                    />
                  }
                  size="sm"
                  color="tertiary"
                  data-testid={testIds?.zoomOut || "map-zoom-out"}
                />
              </MapControlPill>
            </MapControlContainer>
          )}
          {mapAction && (
            <MapActionButtonContainer>
              <MapActionButton
                onClick={mapAction.action}
                color="tertiary"
                size="sm"
                data-testid={mapAction.testId || "map-action-button"}
              >
                {mapAction.title}
              </MapActionButton>
            </MapActionButtonContainer>
          )}
          {showEditRevert &&
            !showMyLocationButton &&
            (isEditMode && !pinMoved ? (
              <MapActionButtonContainer>
                <MovePinSpan>
                  <Typography
                    style={{
                      color: WHITE,
                      textAlign: "center",
                      opacity: 1,
                    }}
                  >
                    Move pin to change location
                  </Typography>
                </MovePinSpan>
              </MapActionButtonContainer>
            ) : (
              <MapActionButtonContainer>
                <MapActionButton
                  onClick={
                    isEditMode
                      ? revertCoords
                      : () => {
                          if (onChangeMode) {
                            onChangeMode(true);
                          }
                          setIsEditMode(true);
                        }
                  }
                  color="tertiary"
                  size="sm"
                  data-testid="map-edit-coords"
                >
                  {isEditMode ? "Reset location" : "Edit location"}
                </MapActionButton>
              </MapActionButtonContainer>
            ))}
        </>
      )}
      <NeighborsPrompt className={showSunrunnersNearMe ? "show" : ""}>
        <div>
          {neighborsMapCoords?.count || neighborsMapCoords?.locations?.length}
        </div>
        <div>Homes within 25 miles have solar from Sunrun.</div>
      </NeighborsPrompt>
    </Container>
  );
};

const MapWindow = styled.div`
  height: 100%;
  width: 100%;
  border-radius: 16px;
  background-color: #eee;
  &.fullscreen {
    border-radius: 0;
  }
  &.gm-style-cc {
    display: none;
  }
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const Container = styled.div`
  position: relative;
  outline: none;
  width: 100%;
  height: 100%;
  font-family: "Roobert", sans-serif;
  &.fullscreen {
    position: fixed;
    top: 0px;
    left: 0px;
    width: 100vw;
    height: 100vh;
    z-index: 1000;
  }
`;

const MapControlContainer = styled.div`
  position: absolute;
  top: 0px;
  right: 0px;
  z-index: 1;
  padding: 16px 17px;
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const MapControlPill = styled.div`
  background-color: ${WHITE};
  padding: 4px 0px;
  border: 0px;
  border-radius: 90px;
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const NoMapContainer = styled.div`
  justify-content: space-around;
  display: flex;
  height: 100%;
`;

const MapActionButtonContainer = styled.div`
  position: absolute;
  bottom: 20px;
  width: 100%;
  z-index: 1;
  display: flex;
  justify-content: space-around;
`;

const MapActionButton = styled(Button)`
  background-color: ${WHITE};
  font-family: var(--font-families-roobert), sans-serif;
`;

const MapControlButton = styled(Button)`
  padding: 0px 4px;
`;

const ShowLocationButton = styled(Button)`
  position: relative;
  z-index: 1;
`;

const MovePinSpan = styled.span`
  padding: 8px 0;
  margin: 0 20px;
  border-radius: 10px;
  color: ${WHITE};
  width: 100%;
  background: rgba(0, 0, 0, 0.5);
`;

const NeighborsPrompt = styled.div`
  &.show {
    opacity: 1;
    z-index: 2;
  }
  transition: 0.3s;
  opacity: 0;
  position: absolute;
  bottom: 0px;
  background-color: #fffbf0;
  display: flex;
  gap: 14px;
  justify-content: center;
  align-items: center;
  max-width: 100%;
  flex-wrap: nowrap;
  min-width: 100%;
  padding: 18px 0px;
  & :first-of-type {
    font-size: 56px;
    font-weight: 500;
    padding-left: 18px;
  }
  & :last-child {
    padding-right: 18px;
    font-family: "Roobert";
    font-style: normal;
    font-weight: 500;
    font-size: 20px;
    line-height: 20px;
    letter-spacing: -0.02em;
    max-width: 200px;
  }
`;

export type { MapProps, ShowNearMeMapProps, BaseMapProps, onDragProps };
export { ResidentMap };
